import { isCurrencyCodeString } from '../currency/types';
/* Parse a purchase option's per-month price, returning undefined for a missing
 * option or an unparseable/empty value so callers can skip it. */
export function parseTierPrice(value) {
  if (value === undefined || value.trim() === '') return undefined;
  const price = Number(value);
  return Number.isFinite(price) && price >= 0 ? price : undefined;
}

/* Resolve the per-month price a tier should show for the selected term. Prefer
 * the selected term, then fall back to any other term that carries a usable
 * price. Generalizing over the term set (instead of enumerating ANNUAL/MONTHLY
 * preference lists) keeps a monthly-only tier (STARTER) showing its monthly
 * price on the annual toggle and an annual-only tier (ENTERPRISE) showing its
 * annual price on the monthly toggle. Mirrors v1's usePerMonthPriceForTerm,
 * which resolves annualPrice || monthlyPrice regardless of requested term.
 *
 * This is the tier's pricing business logic, kept out of presentational
 * components: the HSoC merch cards and the FSC configure tier selector only
 * ever receive the resolved number to render. */
export function resolveTierMonthlyPrice(pricing, selectedTerm) {
  if (!pricing) return undefined;
  const {
    purchaseOptions
  } = pricing;
  for (const option of [purchaseOptions[selectedTerm], ...Object.values(purchaseOptions)]) {
    const price = parseTierPrice(option === null || option === void 0 ? void 0 : option.monthlyPrice);
    if (price !== undefined) return price;
  }
  return undefined;
}
export function buildTierPriceDisplay(pricing, selectedTerm, currencyCode, tierName) {
  if (!isCurrencyCodeString(currencyCode)) return undefined;

  /* The FREE tier has no purchase options; mirror the public pages and show an
   * explicit $0 rather than no price. */
  if (tierName === 'FREE') return {
    price: 0,
    currencyCode
  };
  const price = resolveTierMonthlyPrice(pricing, selectedTerm);
  return price === undefined ? undefined : {
    price,
    currencyCode
  };
}
export function hasMultipleBillingTerms(productsByTier) {
  if (!productsByTier) return false;
  return Object.values(productsByTier).some(tier => Object.keys(tier.pricing.purchaseOptions).length > 1);
}